// Katalog ağaç tablosu (mockup 09): kategori bantları açılır/kapanır, satır: etiket (tek satır,
// kırpılır — `CatalogNameCell`), kod (ayrı soluk mono sütun), birim, varsayılan formül (aktif varyant
// ifadesi ya da "—"), açıklama; türetilmiş satırda ƒx.
// Kullanımdan kalkmış metrik soluk + rozet. Satır tıklaması → MetricModal (üst bileşen).
// Finar rev-B 10: `onDeprecate` verilirse (kpi.manage) etkin YAPRAK satırın ⋯ menüsünde
// "Kullanımdan kaldır" (onay üst bileşende). Yeniden etkinleştirme ucu yok — kalkmış satırda öğe yok.
// Kırılım (spec §6.4, §18 DR8/DR14): satır türü shared `rowKindsOf` (kategori bandı / toplam kalın
// 0.85rem·700 + tek biçim rozet "kırılım: cinsiyet (2) · …" / grup ince bant 0.75rem·600 text-3 +
// %40 üst kenar, aç/kapanmaz / üye normal, birim "(üstten)"); `node_kind` rozeti yok. `onConvert`
// verilirse (kpi.manage) kategoride kalıp (a), kardeşli göstergede kalıp (b) ⋯ "Kırılıma dönüştür";
// global kategori `kpi.catalog.categories` ister — yoksa pasif + "Yalnız Sistem Yöneticisi".
// Sektör paketleri (§5 Katalog, DR15/DR17): `packages` verilirse satır sonunda paket rozet kolonu
// (≤1100 px gizli, rozetler adın altında — `globals.css` `.kpi-catalog-pkg-*`) + süzgeç görünürlüğü.
// Seçim kutusu kolonu + "Pakete ekle" 2026-11-03 ürün kararıyla kaldırıldı (Paketler ekranı tek yol).
// Sürükle-bırak sıralama (rev-2 #4b/#12b/#14, R6-1): `onReorder` + `kpi.manage` varken TÜM etkin
// satırlar (kategoriler dahil — kardeş sırası hiyerarşi değiştirmez, kategori izni gerekmez) AYNI
// üst içinde sürüklenir (grup/üye kendi grubunda); kalkmış satır sürüklenmez (sunucu sona koyar).
// Satır başında ⋮⋮ tutamacı = klavye ↑↓ eşdeğeri; ipucu (aria-describedby) yalnız tutamakta.
// Yetki yoksa biçimleme birebir eski hâliyle kalır.
// K2 (2026-11-03): yan kaydırma yok — `table-layout: fixed` + sütun genişlikleri (tutamaç 44 · ad
// kalan alan · kod 140 · birim 80 · formül 170 · açıklama %14 · paketler 160 · ⋯ 44 — brief'teki
// "ad 1fr / açıklama 1fr" 1440 px'te adı ~200 px'e sıkıştırıyordu, ad önceliklidir); metin hücreleri tek
// satır kırpılır + `title`; satır eylemleri tek ⋯ menüsünde (`CatalogRowMenu`). Ara kırılımlar
// (fix round — Gösterge sütunu her genişlikte ≥240 px; Chromium sabit sütunlarda taşmak yerine adı
// eziyordu): ≤1340 px Açıklama, ≤1199 px Kod, ≤1100 px Paketler sütunu gizli (rozetler adın altında)
// — `globals.css` `.kpi-catalog-desc-col` / `-code-col` / `-pkg-col`.
// K4 (2026-11-03): iç kaydırıcı + sanal pencere YOK — tüm satırlar çizilir, tablo sayfayla uzar
// (Hedefler/Disclosure gibi). Yapışkan başlık sayfa kaydırmasında AppShell main yüzünden çalışmaz
// (TODO-UX-32).

import { useMemo, useState } from "react";
import type { KpiMetric, Permissions } from "@mahir/shared";
import { totalPillText } from "../../lib/kpi-breakdown-set";
import { KPI_BREAKDOWN_COPY } from "../../lib/kpi-breakdown-copy";
import {
  catalogBreakdownView,
  convertActionFor,
  type ConvertPattern,
} from "../../lib/kpi-catalog-breakdown";
import { filterCatalogRows, packageBadgesFor } from "../../lib/kpi-catalog-packages";
import { KPI_PACKAGES_COPY } from "../../lib/kpi-packages-copy";
import { activeOrderPlan, moveBy, type CatalogReorderPlan } from "../../lib/kpi-reorder";
import {
  buildIndicatorTree,
  planTreeReorder,
  siblingsInTree,
  visibleIdsForSearch,
} from "../../lib/kpi-set-editor";
import { KPI_COPY } from "../../lib/kpi-ui";
import { useRowDrag } from "../../hooks/use-row-drag";
import { CatalogRowGrip } from "./CatalogRowGrip";
import { CatalogNameCell, catalogLabelEllipsis } from "./CatalogNameCell";
import { CatalogPackageBadges } from "./CatalogPackageBadges";
import { CatalogRowMenu, type CatalogRowMenuItem } from "./CatalogRowMenu";
import { bandRowBg, btnGhost, monoStyle, selectedRowBg, tdStyle, thStyle } from "./kpi-form-styles";

export interface CatalogTableProps {
  metrics: KpiMetric[];
  /** kök kategori filtresi (null = Tümü) */
  rootId: string | null;
  search: string;
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** rev-B 10 — satırdan hızlı kaldırma (yalnız kpi.manage); yoksa sütun çizilmez */
  onDeprecate?: ((metric: KpiMetric) => void) | undefined;
  /** kırılıma dönüştür (yalnız kpi.manage); yoksa eylem çizilmez */
  onConvert?: ((metric: KpiMetric, pattern: ConvertPattern) => void) | undefined;
  /** eylem kapısı (kpi.manage + kategori izni) */
  permissions?: Permissions | null | undefined;
  /** paket rozetleri + süzgeç (verilmezse kolon çizilmez) */
  packages?: CatalogTablePackages | undefined;
  /** sürükle-bırak / ↑↓ sıralama: üst + etkin kardeşlerin yeni sırası (yalnız kpi.manage) */
  onReorder?: ((parentId: string | null, plan: CatalogReorderPlan) => void) | undefined;
  /** sıra kaydediliyor — sürükleme ve tutamaç pasif */
  reorderBusy?: boolean | undefined;
}

export interface CatalogTablePackages {
  membership: Readonly<Record<string, readonly string[]>>;
  /** etkin paketler (kimlik → ad), liste sırası */
  namesById: ReadonlyMap<string, string>;
  /** süzgeçte seçili paketler (boş = süzgeç yok) */
  filterIds: readonly string[];
  /** süzgeç görünürlüğü (üyeler + ataları); null = süzgeç yok */
  visibleIds: ReadonlySet<string> | null;
}

const groupBorder = "1px solid color-mix(in srgb, var(--color-border) 40%, transparent)";

export function CatalogTable(props: CatalogTableProps): JSX.Element {
  const { metrics, rootId, search, selectedId, onSelect, onDeprecate, onConvert } = props;
  const { packages: pkgs, onReorder, permissions } = props;
  const hasActions = Boolean(onDeprecate ?? onConvert);
  const canReorder = Boolean(onReorder) && permissions?.["kpi.manage"] === true;
  // temel sütunlar: Gösterge · Kod · Birim · Varsayılan formül · Açıklama (+ ⋯ / Paketler / tutamaç)
  const cols = (hasActions ? 6 : 5) + (pkgs ? 1 : 0) + (canReorder ? 1 : 0);
  const tree = useMemo(() => buildIndicatorTree(metrics), [metrics]);
  const movable = (id: string): boolean => tree.byId.get(id)?.node.status !== "deprecated";
  const commit = (id: string, changes: Map<string, number> | null): void => {
    const sib = siblingsInTree(tree, id);
    const plan = sib && changes ? activeOrderPlan(sib.siblings, changes) : null;
    if (sib && plan) onReorder?.(sib.parentKey, plan);
  };
  const drag = useRowDrag({
    enabled: canReorder && !props.reorderBusy,
    parentOf: (id) => siblingsInTree(tree, id)?.parentKey,
    canDrag: movable,
    canDrop: (a, b, pos) => planTreeReorder(tree, a, b, pos) !== null,
    onDrop: (a, b, pos) => commit(a, planTreeReorder(tree, a, b, pos)),
    describeRows: false,
  });
  const step = (id: string, direction: -1 | 1): void => {
    const sib = siblingsInTree(tree, id);
    if (sib) commit(id, moveBy(sib.siblings, id, direction));
  };
  const view = useMemo(() => catalogBreakdownView(metrics), [metrics]);
  const visible = useMemo(() => visibleIdsForSearch(tree, search), [tree, search]);
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const packageVisible = pkgs?.visibleIds ?? null;

  const rows = useMemo(
    () =>
      filterCatalogRows(tree, {
        rootId,
        searchVisible: visible,
        packageVisible,
        collapsed,
      }),
    [tree, rootId, visible, packageVisible, collapsed],
  );

  const categoryIds = useMemo(
    () =>
      tree.rows
        .filter((r) => r.hasChildren && view.kindOf(r.node.id) !== "breakdown_group")
        .map((r) => r.node.id),
    [tree, view],
  );

  const C = KPI_COPY.catalogDeprecate;
  const clip = { ...tdStyle, ...catalogLabelEllipsis };
  return (
    <div>
      <div
        className="flex items-center gap-2 px-3 py-2"
        style={{ borderBottom: "1px solid var(--color-border)" }}
      >
        <span className="text-xs" style={{ color: "var(--color-text-3)" }}>
          Satıra tıkla → düzenle
        </span>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          style={{ ...btnGhost, minHeight: 32, padding: "4px 10px", fontSize: "0.8rem" }}
          onClick={() => setCollapsed(new Set())}
        >
          Tümünü gör
        </button>
        <button
          type="button"
          style={{ ...btnGhost, minHeight: 32, padding: "4px 10px", fontSize: "0.8rem" }}
          onClick={() => setCollapsed(new Set(categoryIds))}
        >
          Kapat
        </button>
      </div>
      <table
        role="treegrid"
        aria-label="Katalog göstergeleri"
        style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}
      >
        <thead>
          <tr>
            {canReorder ? (
              <th style={{ ...thStyle, width: 44, padding: "8px 0" }}>
                <span className="sr-only">{KPI_COPY.reorder.grip}</span>
              </th>
            ) : null}
            <th style={thStyle}>Gösterge</th>
            <th className="kpi-catalog-code-col" style={{ ...thStyle, width: 140 }}>
              Kod
            </th>
            <th style={{ ...thStyle, width: 80 }}>Birim</th>
            <th style={{ ...thStyle, width: 170 }}>Varsayılan formül</th>
            <th className="kpi-catalog-desc-col" style={{ ...thStyle, width: "14%" }}>
              Açıklama
            </th>
            {pkgs ? (
              <th className="kpi-catalog-pkg-col" style={{ ...thStyle, width: 160 }}>
                {KPI_PACKAGES_COPY.catalog.column}
              </th>
            ) : null}
            {hasActions ? (
              <th style={{ ...thStyle, width: 44, padding: "8px 0" }}>
                <span className="sr-only">Eylemler</span>
              </th>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td style={{ ...tdStyle, color: "var(--color-text-3)" }} colSpan={cols}>
                {packageVisible
                  ? KPI_PACKAGES_COPY.catalog.noMatchInPackages
                  : "Eşleşen gösterge yok."}
              </td>
            </tr>
          ) : null}
          {rows.map((r) => {
            const m = r.node;
            const kind = view.kindOf(m.id);
            const isGroup = kind === "breakdown_group";
            const isTotal = kind === "total";
            const isMember = view.isMember(m.id);
            const isCat = r.hasChildren && !isTotal && !isGroup;
            const toggles = r.hasChildren && !isGroup;
            const convert = onConvert
              ? convertActionFor(m, metrics, view, props.permissions)
              : null;
            const open = !collapsed.has(m.id);
            const selected = m.id === selectedId;
            const active = m.defaultFormulas.find((f) => f.isActive);
            const deprecated = m.status === "deprecated";
            const badges = pkgs
              ? packageBadgesFor(m.id, pkgs.membership, pkgs.namesById, pkgs.filterIds)
              : null;
            const dragProps = drag.rowProps(m.id);
            const unit =
              isCat || isGroup
                ? ""
                : isMember
                  ? KPI_BREAKDOWN_COPY.tree.inheritedUnit
                  : (m.defaultUnit ?? "—");
            const formula = isCat ? "" : (active?.expression ?? "—");
            const menu: CatalogRowMenuItem[] = [];
            if (convert)
              menu.push({
                key: "convert",
                label: KPI_BREAKDOWN_COPY.catalog.convertAction,
                ariaLabel: KPI_BREAKDOWN_COPY.catalog.convertAria(m.label),
                testId: "kpi-catalog-row-convert",
                disabled: !convert.enabled,
                title: convert.hint ?? undefined,
                onSelect: () => onConvert?.(m, convert.pattern),
              });
            // rev-B S1 — yalnız etkin YAPRAK: kategori kalkınca çocukları köke dağılır (kalkmışlar
            // listelenmez); kategori için pencere yolu kalır
            if (onDeprecate && !deprecated && !r.hasChildren)
              menu.push({
                key: "deprecate",
                label: C.action,
                ariaLabel: C.aria(m.label),
                testId: "kpi-catalog-row-deprecate",
                tone: "danger",
                onSelect: () => onDeprecate(m),
              });
            return (
              <tr
                key={m.id}
                {...(dragProps ?? {})}
                role="row"
                aria-level={r.depth + 1}
                aria-selected={selected}
                tabIndex={0}
                onClick={() => onSelect(m.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onSelect(m.id);
                  }
                }}
                style={{
                  cursor: "pointer",
                  background: selected ? selectedRowBg : isCat ? bandRowBg : "transparent",
                  opacity: deprecated ? 0.6 : 1,
                  ...(isGroup ? { borderTop: groupBorder } : {}),
                }}
              >
                {canReorder ? (
                  <CatalogRowGrip
                    label={m.label}
                    hintId={movable(m.id) ? drag.hintId : null}
                    disabled={props.reorderBusy === true}
                    onStep={(d) => step(m.id, d)}
                  />
                ) : null}
                <CatalogNameCell
                  metric={m}
                  kind={isMember ? "member" : kind}
                  isCategory={isCat}
                  depth={r.depth}
                  toggle={
                    toggles
                      ? {
                          open,
                          onToggle: () =>
                            setCollapsed((prev) => {
                              const next = new Set(prev);
                              if (next.has(m.id)) next.delete(m.id);
                              else next.add(m.id);
                              return next;
                            }),
                        }
                      : null
                  }
                  pill={isTotal ? totalPillText({ ...m, metricId: m.id }, view) : null}
                  leafCount={isCat ? r.leafCount : null}
                  badges={badges}
                />
                <td
                  className="kpi-catalog-code-col"
                  data-testid="kpi-catalog-code"
                  style={{
                    ...clip,
                    ...monoStyle,
                    fontSize: "0.78rem",
                    color: "var(--color-text-3)",
                  }}
                  title={isCat ? undefined : m.code}
                >
                  {isCat ? "" : m.code}
                </td>
                <td style={clip} title={unit || undefined}>
                  {unit}
                </td>
                <td
                  style={{ ...clip, ...monoStyle, color: "var(--color-text-2)" }}
                  title={active?.expression ?? undefined}
                >
                  {formula}
                </td>
                <td
                  className="kpi-catalog-desc-col"
                  style={{ ...clip, color: "var(--color-text-2)", fontSize: "0.78rem" }}
                  title={m.description ?? undefined}
                >
                  {m.description ?? ""}
                </td>
                {badges ? (
                  <td className="kpi-catalog-pkg-col" style={tdStyle}>
                    <CatalogPackageBadges badges={badges} metricLabel={m.label} />
                  </td>
                ) : null}
                {hasActions ? (
                  <td style={{ ...tdStyle, padding: "4px 0", textAlign: "center" }}>
                    <CatalogRowMenu rowLabel={m.label} items={menu} />
                  </td>
                ) : null}
              </tr>
            );
          })}
        </tbody>
      </table>
      {canReorder ? (
        <span id={drag.hintId} className="sr-only">
          {KPI_COPY.reorder.hint}
        </span>
      ) : null}
    </div>
  );
}
